import { Injectable } from '@angular/core';

import { mergeQueryParts, QueryPart, QueryPartStrategy } from '@stylehaven/components';
import { Myna } from 'myna-parser';
import { LuceneGrammar } from './lucene-myna-grammer.helper';

@Injectable({ providedIn: 'root' })
export class LuceneParser {
    private Myna: any = Myna;
    private luceneGrammar: any;

    constructor() {
        this.luceneGrammar = new LuceneGrammar();
        this.Myna.registerGrammar('lucene', this.luceneGrammar, this.luceneGrammar.query);
    }

    // should check if legacy lucene facets are present (outer scope?)
    public luceneQueryToQueryParts(query: string): QueryPart[] {
        const ast: any = this.Myna.parse(this.luceneGrammar.query, query);

        let parsed: QueryPart[] | QueryPart = this.parseAST(ast);

        if (!Array.isArray(parsed)) {
            parsed = [parsed];
        }

        // Remove undefined
        const filtered: QueryPart[] = parsed.filter((queryPart: QueryPart) => !!queryPart);

        const merged: QueryPart[] = mergeQueryParts(filtered);

        return merged;
    }

    private parseAST(ast: any): QueryPart[] | QueryPart {
        switch (ast.name) {
            case 'query':
            case 'terms':
                this.checkForChildren(ast);
                return this.unpack(ast.children.map((child: any) => this.parseAST(child)));
            case 'leafTerm':
                return this.handleLeafTerm(ast);
            case 'matchAllNested':
                return this.handleSimpleNest(ast, QueryPartStrategy.MatchAll);
            case 'matchAtLeastOne':
                return this.handleSimpleNest(ast, QueryPartStrategy.MatchAtLeastOne);
            case 'matchNoneNested':
                return this.handleSimpleNest(ast, QueryPartStrategy.MatchNone);
            default:
                throw this.notSupported(ast);
        }
    }

    private checkForChildren(ast: any): void {
        if (ast.children.length === 0 && (ast.allText.trim() !== '' || ast.input.trim() !== '')) {
            throw this.notSupported(ast);
        }
    }

    private handleSimpleNest(ast: any, strategy: QueryPartStrategy): QueryPart {
        let field: string | undefined;
        const values: string[] = [];

        ast.children.forEach((child: any) => {
            if (child.name === 'fieldName') {
                field = child.allText;
            }
            if (child.name === 'termValue') {
                values.push(this.sanitizeValue(child.allText));
            }
        });

        // Fail if no value
        if (!values.length || typeof field === 'undefined') {
            throw this.notSupported(ast);
        }

        return new QueryPart({
            field,
            strategy,
            values: values.map(value => value)
        });
    }

    private handleLeafTerm(ast: any): QueryPart {
        let field = 'q';
        let value: string | undefined;
        let strategy: QueryPartStrategy = QueryPartStrategy.MatchAll;

        ast.children.forEach((child: any) => {
            if (child.name === 'modifier') {
                strategy = child.allText === '+' ? QueryPartStrategy.MatchAll : QueryPartStrategy.MatchNone;
            }
            if (child.name === 'valueModifier') {
                if (child.allText === '-') {
                    throw this.notSupported(ast);
                }
            }
            if (child.name === 'fieldName') {
                field = child.allText;
            }
            if (child.name === 'termValue') {
                if (this.childIsType(child, 'range') && !this.isRangeValid(child)) {
                    throw this.notSupported(ast);
                }

                value = this.sanitizeValue(child.allText);
            }
        });

        // Fail if no value
        if (typeof value === 'undefined') {
            throw this.notSupported(ast);
        }

        return new QueryPart({
            field,
            strategy,
            values: [value]
        });
    }

    private childIsType(ast: any, type: string, childIndex: number = 0): boolean {
        if (ast.children.length > childIndex) {
            return ast.children[childIndex].name === type;
        }
        return false;
    }

    private isRangeValid(termValueNode: any): boolean {
        if (termValueNode.children < 0) {
            return false;
        }
        const range: any = termValueNode.children[0];
        if (range.children < 0) {
            return false;
        }
        const namedRange: any = range.children[0];
        if (namedRange.children < 2) {
            return false;
        }
        const [start, end] = namedRange.children;

        const validNames: string[] = ['wildcard', 'iso8601'];

        const startValid: boolean = validNames.includes(start.name);
        const endValid: boolean = validNames.includes(end.name);
        const bothWildcard: boolean = start.name === end.name && start.name === 'wildcard';
        return !bothWildcard && startValid && endValid;
    }

    private unpack<T>(node: T | T[]): T | T[] {
        if (Array.isArray(node) && node.length === 1) {
            return this.unpack(node[0]);
        }

        return node;
    }

    private sanitizeValue(value: string): string {
        const unescaped: string = this.unescapeValue(value);
        const unquoted: string = this.unquoteValue(unescaped);
        return unquoted;
    }

    private unescapeValue(escapedValue: string): string {
        return escapedValue.replace(/\\(.)/g, '$1');
    }

    private unquoteValue(value: string): string {
        return value.replace(/^['"](.*)['"]$/g, '$1');
    }

    private notSupported(ast: any): void {
        const errorSource: string = ast.allText ?? ast.input;

        throw new Error('AST parse error: ' + ast.name + ' is not yet supported, error at ' + errorSource);
    }
}
